import { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { get } from 'core/libs/lodash';

import { resolveScopedStyles } from 'core/utils/styled-jsx';
import Image from 'core/components/Image';
import CaptionCredits from 'core/components/CaptionCredits';
import withFullscreen from 'core/hocs/withFullscreen';

import styles from './index.styl';

function SideBySide(block) {
  const {
    theme: {
      controls: {
        vikontSideBySide: {
          image: {
            aspectRatio,
            maxWidth,
            interimageSpacing,
            useVerticalImages,
            leftImage: {
              borderRadius: leftBorderRadius,
            },
            rightImage: {
              borderRadius: rightBorderRadius,
            },
          },
          caption: {
            useVerticalCaptions,
            sideColor: sideCaptionColor,
            margin: captionMargin,
            creditsPrefix: creditsPrefix,
          },
        },
        captionCredits: {
          caption: {
            font: captionFont,
          },
        },
      },
    },
    onImageClick,
  } = block;

  const {
    image_left: imageLeft,
    image_right: imageRight,
  } = block.attributes;


  const leftCover = get(imageLeft, 'attributes.versions.original');
  const previewCoverLeft = get(imageLeft, 'attributes.versions.thumbnail');
  const leftCaption = get(imageLeft, 'attributes.caption');
  const leftCredits = get(imageLeft, 'attributes.credits');
  const leftAlt = get(imageLeft, 'attributes.alt');

  const rightCover = get(imageRight, 'attributes.versions.original');
  const previewCoverRight = get(imageRight, 'attributes.versions.thumbnail');
  const rightCaption = get(imageRight, 'attributes.caption');
  const rightCredits = get(imageRight, 'attributes.credits');
  const rightAlt = get(imageRight, 'attributes.alt');

  const leftOffset = block.attributes.asset_offset_left || 0;
  const rightOffset = block.attributes.asset_offset_right || 0;
  const assetsDistance = block.attributes.assets_distance || interimageSpacing;

  const bottomMargin = Math.max(leftOffset, rightOffset) + 24;
  const halfDistance = assetsDistance / 2;

  const handleImageClick = useCallback((side) => () => {
    if (side === 'left') {
      onImageClick({ cover: leftCover, caption: leftCaption, credits: leftCredits, creditsPrefix });
    } else {
      onImageClick({ cover: rightCover, caption: rightCaption, credits: rightCredits, creditsPrefix });
    }
  }, [onImageClick, leftCover, leftCaption, leftCredits, rightCover, rightCaption, rightCredits, creditsPrefix]);

  const scope = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .sideCaption
          font ${captionFont}
          color ${sideCaptionColor}
      `}</style>
      <style jsx>{`
        .sideCaption
          margin-right 5px
        .caption
          margin ${captionMargin}
      `}</style>
    </scope>
  );

  return (
    <Fragment>
      <style jsx>{`
        .${styles.sideBySide}
          :global(.desktop) &
            margin-bottom ${bottomMargin}px
        .${styles.leftBlock}
          :global(.desktop) &
            transform translate(${halfDistance * -1}px, ${leftOffset}px)
        .${styles.rightBlock}
          :global(.mobile) &
            margin-left ${interimageSpacing}px
          :global(.desktop) &
            transform translate(${halfDistance}px, ${rightOffset}px)
        .${styles.leftImage}
          border-radius ${leftBorderRadius}
        .${styles.rightImage}
          border-radius ${rightBorderRadius}
      `}</style>
      <div className={cx(styles.sideBySide, {
        [styles._vertical]: useVerticalImages,
      })}
      >
        <div className={styles.leftBlock}>
          <div className={styles.leftImage}>
            <Image
              src={leftCover}
              previewSrc={previewCoverLeft}
              aspectRatio={aspectRatio}
              maxWidth={maxWidth}
              alt={leftAlt}
              onClick={handleImageClick('left')}
            />
          </div>
          {!useVerticalCaptions && (
            <CaptionCredits
              caption={leftCaption}
              credits={leftCredits}
              creditsPrefix={creditsPrefix}
              className={cx(styles.leftCaption, scope.wrapClassNames('caption'))}
            />
          )}
        </div>

        <div className={styles.rightBlock}>
          <div className={styles.rightImage}>
            <Image
              src={rightCover}
              previewSrc={previewCoverRight}
              aspectRatio={aspectRatio}
              maxWidth={maxWidth}
              alt={rightAlt}
              onClick={handleImageClick()}
            />
          </div>
          {!useVerticalCaptions && (
            <CaptionCredits
              caption={rightCaption}
              credits={rightCredits}
              creditsPrefix={creditsPrefix}
              className={cx(styles.rightCaption, scope.wrapClassNames('caption'))}
            />
          )}
        </div>
      </div>
      {useVerticalCaptions && (
        <Fragment>
          <CaptionCredits
            caption={leftCaption}
            credits={leftCredits}
            creditsPrefix={creditsPrefix}
            captionPrefix={() => <span className={scope.wrapClassNames('sideCaption')}>Слева:</span>}
          />
          <div className={styles.rightCaption}>
            <CaptionCredits
              caption={rightCaption}
              credits={rightCredits}
              creditsPrefix={creditsPrefix}
              captionPrefix={() => <span className={scope.wrapClassNames('sideCaption')}>Справа:</span>}
            />
          </div>
        </Fragment>
      )}
      <scope.styles />
    </Fragment>
  );
}


SideBySide.propTypes = {
  block: PropTypes.object,
  parentProps: PropTypes.object,
};

export default withFullscreen(SideBySide);
